/**
 * New-item route — thin shim mounting `<ItemComposerPage />`.
 *
 * The composer owns the form state, validation, and submit flow.
 * Edit-mode lives at `$itemId.edit.tsx` and reuses the same component
 * with `loadItemId` set. `?duplicateFrom=<uuid>` seeds the composer
 * from an existing item with SKU cleared.
 *
 * @see apps/web/src/features/items/new/item-composer-page.tsx
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { hasPermission } from "@flint/api-contract";
import { ItemComposerPage } from "../../../../features/items/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { meQueryOptions } from "../../../../lib/session.ts";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface NewItemSearch {
  duplicateFrom?: string;
}

export const Route = createFileRoute("/(workspace)/sales/items/new")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "items.manage")) {
      throw notFound();
    }
  },
  component: NewItemRoute,
  validateSearch: (s: Record<string, unknown>): NewItemSearch => {
    const out: NewItemSearch = {};
    if (typeof s.duplicateFrom === "string" && UUID_RE.test(s.duplicateFrom)) {
      out.duplicateFrom = s.duplicateFrom;
    }
    return out;
  },
});

function NewItemRoute() {
  const search = Route.useSearch();
  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Sales", to: "/" },
        { label: "Items", to: "/sales/items" },
        { label: search.duplicateFrom ? "Duplicate item" : "New item" },
      ]}
      disableViewportScroll
      contentClassName="max-w-none h-full min-h-0 overflow-hidden"
      viewportClassName="p-0 min-h-0 overflow-hidden"
    >
      <ItemComposerPage {...(search.duplicateFrom ? { duplicateFromId: search.duplicateFrom } : {})} />
    </WorkspaceShell>
  );
}
